import {
  useState,
  useEffect,
  useCallback,
  type RefObject,
  type CSSProperties, // Import CSSProperties!
} from 'react';

// Configuration Constants (Adjustable Magic Numbers!)
const DEFAULT_MAX_WIDTH = 280; // Default max-width if not provided
const DEFAULT_MAX_HEIGHT = 300; // Default max-height
const VIEWPORT_PADDING = 10; // Padding from screen edges
const DROPDOWN_MARGIN = 6; // Vertical margin from trigger
const MIN_DROPDOWN_HEIGHT = 100; // Minimum space needed vertically
const OPTION_HEIGHT_ESTIMATE = 36; // For upward opening calculation
const CONTAINER_PADDING_BORDER_ESTIMATE = 4; // For upward opening calculation
const INPUT_OFFSET_UPWARD = 10; // Extra offset when opening upward

// Magical position spell! ✨ (Upgraded Version!)
export function useDropdownPosition(
  triggerRef: RefObject<HTMLElement>,
  isOpen: boolean,
  optionsLength: number,
  maxWidth: number = DEFAULT_MAX_WIDTH, // Add maxWidth parameter with default!
) {
  // State now holds the style object directly! Much cleaner, ze~!
  const [dropdownStyle, setDropdownStyle] = useState<CSSProperties>({});
  const [opensUpward, setOpensUpward] = useState(false);

  const updatePosition = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const scrollY = window.scrollY; // For fixed positioning top offset
      const scrollX = window.scrollX; // For fixed positioning left/right offset

      // --- Vertical Placement Logic (Mostly the same, but cleaner) ---
      const spaceBelow = vh - rect.bottom;
      const spaceAbove = rect.top;

      let calculatedTop: number;
      let calculatedMaxHeight = DEFAULT_MAX_HEIGHT; // Start with default
      let shouldOpenUp = false;

      const estimatedContentHeight =
        optionsLength * OPTION_HEIGHT_ESTIMATE + CONTAINER_PADDING_BORDER_ESTIMATE;

      const canOpenBelow = spaceBelow >= MIN_DROPDOWN_HEIGHT;
      const canOpenAbove = spaceAbove >= MIN_DROPDOWN_HEIGHT;

      if (canOpenBelow || !canOpenAbove) {
        // Prefer below, or force below if cannot open above
        shouldOpenUp = false;
        calculatedMaxHeight = Math.max(
          MIN_DROPDOWN_HEIGHT, // Ensure minimum height
          Math.min(DEFAULT_MAX_HEIGHT, spaceBelow - VIEWPORT_PADDING), // Respect max height and viewport padding
        );
        calculatedTop = rect.bottom + scrollY + DROPDOWN_MARGIN;
      } else {
        // Open above
        shouldOpenUp = true;
        calculatedMaxHeight = Math.max(
          MIN_DROPDOWN_HEIGHT, // Ensure minimum height
          Math.min(DEFAULT_MAX_HEIGHT, spaceAbove - VIEWPORT_PADDING), // Respect max height and viewport padding
        );
        // Calculate actual height needed for positioning above accurately
        const actualDropdownHeight = Math.min(estimatedContentHeight, calculatedMaxHeight);
        calculatedTop =
          rect.top + scrollY - actualDropdownHeight - DROPDOWN_MARGIN - INPUT_OFFSET_UPWARD;
      }

      // --- Horizontal Placement Logic (The New Magic!) ---
      const styleUpdate: CSSProperties = {
        position: 'fixed', // Essential for viewport-relative positioning
        top: `${calculatedTop}px`,
        maxHeight: `${calculatedMaxHeight}px`,
        maxWidth: `${maxWidth}px`, // Use the maxWidth parameter!
        zIndex: 9999, // Keep it on top!
        // Width will be determined by content up to maxWidth
      };

      const spaceRight = vw - rect.left; // Space from trigger left edge to viewport right edge
      const spaceLeft = rect.right; // Space from viewport left edge to trigger right edge

      // Check if dropdown fits starting at trigger's left edge
      const fitsOnRight = spaceRight >= maxWidth + VIEWPORT_PADDING;
      // Check if dropdown fits ending at trigger's right edge
      const fitsOnLeft = spaceLeft >= maxWidth + VIEWPORT_PADDING;

      if (fitsOnRight || spaceRight >= spaceLeft) {
        // Prioritize right side if it fits, or if it has more space than left
        // Align dropdown's left to trigger's left
        styleUpdate.left = `${rect.left + scrollX}px`;
        // Ensure it doesn't overflow right edge if trigger is near the edge
        styleUpdate.right = 'auto'; // Explicitly unset right
      } else if (fitsOnLeft) {
        // If it doesn't fit well on the right, but fits on the left
        // Align dropdown's right to trigger's right
        styleUpdate.right = `${vw - rect.right - scrollX}px`;
        styleUpdate.left = 'auto'; // Explicitly unset left
      } else {
        // Fallback: Doesn't fit perfectly on either side (screen too small?)
        // Default to aligning left, potentially overflowing right edge slightly
        // Or you could center it, but left-align is simpler.
        styleUpdate.left = `${rect.left + scrollX}px`;
        styleUpdate.right = 'auto';
        // Could add logic here to shrink maxWidth if needed, but let's keep it simple first.
        console.warn('Dropdown might overflow screen horizontally, ze~!');
      }

      // --- Update State ---
      setDropdownStyle(styleUpdate);
      setOpensUpward(shouldOpenUp);
    }
  }, [triggerRef, optionsLength, maxWidth]); // Add maxWidth to dependencies!

  useEffect(() => {
    if (isOpen && triggerRef.current) {
      updatePosition(); // Initial position calculation

      // Use capture phase for scroll listener to catch scrolls inside scrollable containers too!
      document.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition, true);

      // Cleanup spell! Poof!
      return () => {
        document.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition, true);
      };
    }
  }, [isOpen, triggerRef, updatePosition]); // updatePosition dependency is correct here

  // Return the style object and direction!
  return { dropdownStyle, opensUpward, updatePosition };
}
